// The quiet "Lumi einrichten" panel on Suite Home: ONE fixed table of the setup
// screens it may point at, in the order it shows them. The module APIs decide
// which entries a person may open (GET /setup-entries answers keys); this file
// only knows where each key leads and which app serves it.
import type { Component } from 'vue';
import { LUMI_ICONS, LUMI_MODULES } from '@lumi/ui';
import { atsApi, apprenticeApi, peopleSelfApi, timeApi } from './api';
import { isModuleAccessible } from './moduleSwitcher';
import { TRAINING_OCCUPATIONS_PATH } from './trainingOccupationsAccess';

// The full-list page ("Was Lumi kann."), a workspace-ui route.
export const SETUP_PAGE_PATH = '/admin/setup';

export type SetupSectionId = 'suite' | 'ats' | 'time' | 'people' | 'apprentice';

// A same-app screen (router) or another app's screen (module + deep link).
export type SetupTarget =
  | { kind: 'route'; path: string }
  | { kind: 'module'; moduleKey: string; deepLink: string };

// `rare`: a rarely needed screen — only the full-list page shows it, never the
// Suite Home panel (which is exactly the non-rare entries).
export type SetupEntryDef = { key: string; target: SetupTarget; rare?: boolean };

export type SetupSectionDef = {
  id: SetupSectionId;
  icon: Component;
  // `suite` is fed by the membership flags; a module section asks that module.
  source: { kind: 'suite' } | { kind: 'module'; moduleKey: string; client: { get: (url: string, cfg?: object) => Promise<{ data: unknown }> } };
  entries: SetupEntryDef[];
};

export const SETUP_SECTIONS: SetupSectionDef[] = [
  {
    id: 'suite',
    // Home's section wears the house, like its pill and launcher tile.
    icon: LUMI_ICONS['home'] as Component,
    source: { kind: 'suite' },
    entries: [
      { key: 'login_branding', target: { kind: 'route', path: '/login-branding' } },
      { key: 'sso', rare: true, target: { kind: 'route', path: '/admin/sso' } },
    ],
  },
  {
    id: 'ats',
    icon: LUMI_MODULES.ats.icon,
    source: { kind: 'module', moduleKey: 'ats', client: atsApi },
    entries: [
      { key: 'portal_design', target: { kind: 'module', moduleKey: 'ats', deepLink: '/portal-settings' } },
      { key: 'email_templates', target: { kind: 'module', moduleKey: 'ats', deepLink: '/email-templates' } },
      { key: 'job_posting_settings', target: { kind: 'module', moduleKey: 'ats', deepLink: '/job-posting-settings' } },
      { key: 'workflows', rare: true, target: { kind: 'module', moduleKey: 'ats', deepLink: '/workflows' } },
      { key: 'application_forms', rare: true, target: { kind: 'module', moduleKey: 'ats', deepLink: '/forms' } },
      { key: 'offer_templates', rare: true, target: { kind: 'module', moduleKey: 'ats', deepLink: '/offer-templates' } },
      { key: 'custom_fields', rare: true, target: { kind: 'module', moduleKey: 'ats', deepLink: '/custom-fields' } },
    ],
  },
  {
    id: 'time',
    icon: LUMI_MODULES.time.icon,
    source: { kind: 'module', moduleKey: 'time_tracking', client: timeApi },
    entries: [
      { key: 'work_time_models', target: { kind: 'module', moduleKey: 'time_tracking', deepLink: '/settings/work-time-models' } },
      { key: 'company_holidays', target: { kind: 'module', moduleKey: 'time_tracking', deepLink: '/settings/company-holidays' } },
      { key: 'booking_types', rare: true, target: { kind: 'module', moduleKey: 'time_tracking', deepLink: '/settings/booking-types' } },
      { key: 'leave_types', rare: true, target: { kind: 'module', moduleKey: 'time_tracking', deepLink: '/settings/leave-types' } },
      { key: 'api_settings', rare: true, target: { kind: 'module', moduleKey: 'time_tracking', deepLink: '/api-settings' } },
      // The time clock itself, the screen that goes on the tablet.
      { key: 'time_clock', rare: true, target: { kind: 'module', moduleKey: 'time_tracking', deepLink: '/kiosk' } },
    ],
  },
  {
    id: 'people',
    icon: LUMI_MODULES.people.icon,
    // The self client: this answer is about the signed-in person, never a worn persona.
    source: { kind: 'module', moduleKey: 'people', client: peopleSelfApi },
    entries: [
      // Straight into People's own import dialog (issue 1058), not the generic
      // data-import page: `?import=1` opens it on the person list.
      { key: 'data_import', target: { kind: 'module', moduleKey: 'people', deepLink: '/persons?import=1' } },
      { key: 'company_profile', target: { kind: 'module', moduleKey: 'people', deepLink: '/company-profile' } },
      { key: 'org_units', target: { kind: 'module', moduleKey: 'people', deepLink: '/org-units' } },
      // The payroll handover to the tax adviser (issue 987) -- People's, not Time's
      // hours list and not Time's API settings ("Schnittstelle"), which stays a rare entry.
      { key: 'payroll_handover', target: { kind: 'module', moduleKey: 'people', deepLink: '/payroll' } },
      { key: 'onboarding_templates', rare: true, target: { kind: 'module', moduleKey: 'people', deepLink: '/onboarding-templates' } },
      { key: 'recruiting_options', rare: true, target: { kind: 'module', moduleKey: 'people', deepLink: '/options' } },
    ],
  },
  {
    id: 'apprentice',
    icon: LUMI_MODULES.apprentice.icon,
    source: { kind: 'module', moduleKey: 'apprenticeship', client: apprenticeApi },
    entries: [
      { key: 'training_occupations', target: { kind: 'route', path: TRAINING_OCCUPATIONS_PATH } },
    ],
  },
];

const REQUEST_TIMEOUT_MS = 8000;

// One module's answer. Anything but a clean `{ entries: string[] }` — a rejection,
// a timeout, an HTML page, a wrong shape — is `null` ("failed", never an error):
// the section is simply absent, the panel is a hint and must not be able to break
// Suite Home. `[]` is a real answer ("nothing for this person"); the caller keeps
// only answers it can tell apart from a failure.
export async function fetchSetupEntryKeys(
  client: { get: (url: string, cfg?: object) => Promise<{ data: unknown }> },
): Promise<string[] | null> {
  try {
    const { data } = await client.get('/setup-entries', { suppressGlobalError: true, timeout: REQUEST_TIMEOUT_MS });
    const entries = (data as { entries?: unknown } | null)?.entries;
    if (!Array.isArray(entries)) return null;
    return entries.filter((k): k is string => typeof k === 'string');
  } catch {
    return null;
  }
}

export type SetupSection = {
  id: SetupSectionId;
  icon: SetupSectionDef['icon'];
  short: SetupEntryDef[];
  rare: SetupEntryDef[];
};

// The module keys (as the tenant's module rows name them) this person can open.
export function accessibleSetupModuleKeys(
  tenant: Parameters<typeof isModuleAccessible>[0] | null | undefined,
): string[] {
  if (!tenant) return [];
  const keys = SETUP_SECTIONS.flatMap((s) => (s.source.kind === 'module' ? [s.source.moduleKey] : []));
  return keys.filter((key) => isModuleAccessible(tenant, key));
}

// Which entries each section shows, from the modules' answers. A section needs at
// least one entry the person may open (short or rare); a module's answer decides
// nothing for a module the person cannot launch. `suiteAdmin`: the suite section
// stands on the membership flags (owner / suite admin), not on a call.
export function resolveSetupSections(
  answers: Record<string, string[]>,
  opts: { suiteAdmin: boolean; accessibleModules: string[] },
): SetupSection[] {
  return SETUP_SECTIONS.map((s) => {
    const allowed = s.source.kind === 'suite' ? (opts.suiteAdmin ? null : []) : answers[s.source.moduleKey] ?? [];
    const keep = s.entries
      .filter((e) => allowed === null || allowed.includes(e.key))
      .filter((e) => e.target.kind !== 'module' || opts.accessibleModules.includes(e.target.moduleKey));
    return { id: s.id, icon: s.icon, short: keep.filter((e) => !e.rare), rare: keep.filter((e) => e.rare) };
  }).filter((s) => s.short.length + s.rare.length > 0);
}

// The setup surfaces (panel, page, sidebar entry) are about the signed-in person's
// own workspace and nobody else's: never in a demo, under a persona mask, for a
// borrowed (support) workspace, or before first-run setup is resolved.
export function setupSurfaceEligible(input: {
  tenant: { supportAccess?: boolean } | null | undefined;
  firstRunResolved: boolean;
  isDemo: boolean;
  isMasked: boolean;
}): boolean {
  return !!input.tenant && input.firstRunResolved && !input.isDemo && !input.isMasked && !input.tenant.supportAccess;
}
